import { useEffect } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/app/store/auth'
import { useTickerStore } from '@/app/store/ticker'
import { useMe } from '@/features/auth/hooks/useAuth'
import { usePresence } from '@/contexts/PresenceContext'
import { useToast } from '@/hooks/use-toast'
import { httpStatusOf, TICKER_MAX_SETS, tickerApi, type TickerPrefs, type TickerRecoverySet } from './api'
import { connectTicker, recheckTickerAccess } from './connection'
import { userCanViewTicker } from './permission'
import i18n from '@/i18n'

export { userCanViewTicker }

export const tickerKeys = {
    all: ['ticker'] as const,
    /** Keyed by user: the preferences are per user, and a sign-out and sign-in may change who. */
    prefs: (userId: number | undefined) => [...tickerKeys.all, 'prefs', userId ?? 0] as const,
    recoverySets: () => [...tickerKeys.all, 'recovery-sets'] as const,
    detail: (id: number) => [...tickerKeys.all, 'notification', id] as const,
    count: (sets: readonly number[], after: number) => [...tickerKeys.all, 'count', sets.join(','), after] as const,
    samples: (setId: number) => [...tickerKeys.all, 'samples', setId] as const,
}

/** A refusal is not retried: 401 is the session ending, 403 hides the ticker (api.ts). */
const retryUnlessRefused = (count: number, error: unknown) => {
    const status = httpStatusOf(error)
    return status !== 403 && status !== 401 && count < 2
}

/**
 * Whether this user may see the ticker at all: `ticker.view` on the /auth/me user. False until
 * /auth/me has answered, so nothing is rendered on a guess. The server enforces the permission
 * on every ticker endpoint and on `ticker:watch`; when it refuses (a 403, or a watch refused for
 * the permission) the store is marked forbidden and the ticker hides until something clears it
 * (see the store's `forbidden`).
 */
export function useTickerPermission(): boolean {
    const { data: me } = useMe()
    const forbidden = useTickerStore((s) => s.forbidden)
    return userCanViewTicker(me) && !forbidden
}

function useAuthReady(): boolean {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const mfaRequired = useAuthStore((s) => s.mfaRequired)
    return isAuthenticated && !mfaRequired
}

/**
 * The caller's ticker preferences, mirrored into the ticker store as they arrive. They need only
 * a sign-in (§5.1), so they say nothing about `ticker.view`.
 */
export function useTickerPrefs(enabled: boolean) {
    const ready = useAuthReady()
    const userId = useAuthStore((s) => s.user?.id)
    const query = useQuery({
        queryKey: tickerKeys.prefs(userId),
        queryFn: tickerApi.getPrefs,
        enabled: enabled && ready,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        retry: retryUnlessRefused,
    })
    useEffect(() => {
        if (query.data) useTickerStore.getState().setPrefs(query.data)
    }, [query.data])
    return query
}

/**
 * Saves run one at a time, in click order (the mutation scope), so the server stores the last
 * choice. Each is optimistic at once; only the NEWEST save's answer is applied or rolled back,
 * so an earlier save's late response never overwrites a newer choice.
 */
let latestSave = 0
/** Names the save, so that useFollowOpenedSets can see one is in flight. */
const SAVE_KEY = [...tickerKeys.all, 'save-prefs'] as const

/**
 * Save the preferences optimistically: the store and the cached query change at once, and both
 * are put back if the server refuses. The server's answer (unknown set ids dropped) wins, when
 * it answers the newest save.
 */
export function useSaveTickerPrefs() {
    const queryClient = useQueryClient()
    const { toast } = useToast()
    const userId = useAuthStore((s) => s.user?.id)
    const key = tickerKeys.prefs(userId)
    return useMutation({
        scope: { id: 'ticker-prefs' },
        mutationKey: SAVE_KEY,
        mutationFn: (prefs: TickerPrefs) => tickerApi.putPrefs(prefs),
        onMutate: async (next: TickerPrefs) => {
            const seq = ++latestSave
            // Read before anything awaits: a second click in the same tick must see this one.
            const previous = queryClient.getQueryData<TickerPrefs>(key) ?? useTickerStore.getState().prefs
            queryClient.setQueryData(key, next)
            useTickerStore.getState().setPrefs(next)
            await queryClient.cancelQueries({ queryKey: key })
            return { previous, seq }
        },
        onError: (_error, _next, context) => {
            // A newer save is on its way, carrying the whole choice; its answer decides.
            if (context?.seq !== latestSave) return
            toast({ title: i18n.t('ticker.saveFailed'), variant: 'destructive' })
            const previous = context?.previous
            if (previous) {
                queryClient.setQueryData(key, previous)
                useTickerStore.getState().setPrefs(previous)
            }
        },
        onSuccess: (saved, _next, context) => {
            if (!saved || context?.seq !== latestSave) return
            queryClient.setQueryData(key, saved)
            useTickerStore.getState().setPrefs(saved)
        },
    })
}

/** How often a user on All recovery sets asks whether a set has opened or closed. */
const ALL_SETS_POLL_MS = 60_000

/**
 * Every named recovery set, with `open`; the dropdown offers only the open ones. With `poll` (the
 * user is on All recovery sets) it is asked again every minute, so a set opened later is noticed
 * on a screen nobody touches - a wall display - and not only when the window regains the focus.
 */
export function useTickerRecoverySets(enabled: boolean, { poll = false }: { poll?: boolean } = {}) {
    const ready = useAuthReady()
    return useQuery({
        queryKey: tickerKeys.recoverySets(),
        queryFn: tickerApi.recoverySets,
        enabled: enabled && ready,
        staleTime: 60_000,
        refetchInterval: poll ? ALL_SETS_POLL_MS : false,
        retry: retryUnlessRefused,
    })
}

/**
 * On All recovery sets a set opened later must start arriving without the user saving anything
 * (Andrew, 29 Sep 2026). The server works All out on every read of the preferences, so when the
 * list of sets changes and its open ones are no longer the sets in effect, the preferences are
 * read again: the store takes the new list, and the connection primes and watches the new set
 * (connection.ts). A set that has closed drops out the same way.
 *
 * It runs when the LIST changes, never when the preferences do, so a difference one read cannot
 * close can never loop. A save in flight is left to decide: its answer carries the sets in effect.
 */
export function useFollowOpenedSets(sets: readonly TickerRecoverySet[] | undefined) {
    const queryClient = useQueryClient()
    const userId = useAuthStore((s) => s.user?.id)
    useEffect(() => {
        const prefs = useTickerStore.getState().prefs
        if (!sets || !prefs?.allSets || queryClient.isMutating({ mutationKey: SAVE_KEY }) > 0) return
        // The server's own rule: the newest open sets, as many as one watch takes.
        const open = sets.filter((set) => set.open === true).map((set) => set.id).slice(0, TICKER_MAX_SETS)
        const have = new Set(prefs.recoverySetIds)
        if (open.length === have.size && open.every((id) => have.has(id))) return
        void queryClient.invalidateQueries({ queryKey: tickerKeys.prefs(userId), exact: true })
    }, [sets, queryClient, userId])
}

/** The full record behind an expanded row. */
export function useNotificationDetail(id: number | null) {
    const ready = useAuthReady()
    return useQuery({
        queryKey: tickerKeys.detail(id ?? 0),
        queryFn: () => tickerApi.notification(id as number),
        enabled: id != null && ready,
        staleTime: Infinity,
        retry: retryUnlessRefused,
    })
}

/**
 * The "N new messages waiting" count above a frozen snapshot: rows newer than its top row (0 for
 * an empty snapshot, so everything counts). Re-asked when the socket brings
 * something (lastSeenId moves) and every 15 s, so it also counts kinds the chosen level keeps off
 * the conveyor.
 */
export function useNewMessageCount(sets: readonly number[], topId: number, enabled: boolean) {
    const ready = useAuthReady()
    const lastSeenId = useTickerStore((s) => s.lastSeenId)
    return useQuery({
        queryKey: [...tickerKeys.count(sets, topId), lastSeenId],
        queryFn: () => tickerApi.count(sets, topId),
        enabled: enabled && ready && sets.length > 0,
        refetchInterval: 15_000,
        // A new key per arrival: keep showing the last count rather than blinking "Up to date".
        placeholderData: keepPreviousData,
        select: (d) => d.count,
        retry: retryUnlessRefused,
    })
}

/** Ride the always-on presence socket for as long as the user may see the ticker. */
export function useTickerConnection(allowed: boolean) {
    const { socket } = usePresence()
    const userId = useAuthStore((s) => s.user?.id)
    useEffect(() => {
        if (!allowed || !socket) return
        const dispose = connectTicker(socket)
        return () => {
            dispose()
            // The cursor, floors and queue belong to this connection's user.
            useTickerStore.getState().resetSession()
        }
    }, [allowed, socket, userId])
}

/**
 * While the ticker is hidden by a REST 403 - which may only have been the server failing to read
 * the role - ask again now and then, and at once when the socket reconnects. A success brings the
 * ticker back (api.ts, onAllowed). Runs only for a user whose /auth/me grants `ticker.view`.
 */
export function useTickerAccessRecheck(enabled: boolean) {
    const { socket } = usePresence()
    useEffect(() => {
        if (!enabled) return
        return recheckTickerAccess(socket ?? null)
    }, [enabled, socket])
}

/**
 * The development-only sample messages of one set (Andrew, 25 Sep 2026: "only available in
 * development the same as system templates"): how many it holds, and - by answering at all -
 * whether this deployment offers them. Where shipped content is not authored the server answers
 * 404, which leaves this in error: not offered. Not retried, and not re-asked on focus: a 404 here
 * is the deployment's setting, not a blip.
 */
export function useTickerSampleCount(setId: number | null, enabled: boolean) {
    const ready = useAuthReady()
    return useQuery({
        queryKey: tickerKeys.samples(setId ?? 0),
        queryFn: () => tickerApi.sampleCount(setId as number),
        enabled: enabled && ready && setId != null,
        retry: false,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
    })
}

/** Write one sample message of each kind for the set; they arrive on the ticker like real ones. */
export function usePopulateTickerSamples(setId: number) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: () => tickerApi.populateSamples(setId),
        onSettled: () => queryClient.invalidateQueries({ queryKey: tickerKeys.samples(setId) }),
    })
}

/**
 * Delete the set's sample messages. Rows already on screen in OTHER browsers stay until those read
 * their history again; this browser drops them from the conveyor's queue, re-reads the history
 * panel and the "new messages" counts, and forgets the expanded records it holds.
 */
export function useClearTickerSamples(setId: number) {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: () => tickerApi.clearSamples(setId),
        onSuccess: () => {
            useTickerStore.getState().samplesCleared(setId)
            queryClient.removeQueries({ queryKey: [...tickerKeys.all, 'notification'] })
            void queryClient.invalidateQueries({ queryKey: [...tickerKeys.all, 'count'] })
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey: tickerKeys.samples(setId) }),
    })
}
